import type { UnreadControl } from '../../lib/api'

/*
 * WHY A CONTROL PRODUCED NO VERDICT, IN WORDS (migration 0159, owner 2026-09-26).
 *
 * The dashboard used to explain every control a scan could not read with one sentence -- "a
 * permission may have changed, or Microsoft may have been unavailable". The same week that was
 * wrong three ways: a Defender permission Defenssive's access lacked, Defender answering with nothing
 * because no device was onboarded, and refusals and throttling that each had a cause of their own.
 * The server now records one fixed word per control, and this file is the one place a page learns
 * how that word reads -- so every surface says the same thing about the same reason.
 *
 * Words are gathered into GROUPS by what the customer can do about them, and the groups are shown
 * actionable first. Two rules hold for every group:
 *  - a group names a cause only as far as the word establishes it. "Microsoft refused" does not say
 *    which of a permission, a role, a licence or an access restriction; it says it cannot tell.
 *  - "nothing needs changing in your tenant" is said only where that is true: Microsoft not
 *    answering. A stop on Defenssive's side is ours to look into, not the tenant's all-clear.
 *
 * A word this build does not know (a newer server) reads as "No usable answer" -- the one group that
 * claims no cause. No word at all is a control recorded before 0159, worded exactly as it was then.
 */

/** A display group. `legacy` is a control recorded before 0159; `waiting` has a card of its own. */
export type UnreadGroupId =
  | 'permission'
  | 'access'
  | 'connection'
  | 'refused'
  | 'plan'
  | 'noDevices'
  | 'noAnswer'
  | 'dns'
  | 'ours'
  | 'unusable'
  | 'unrecorded'
  | 'legacy'
  | 'waiting'

/** Every word the server may record (0159), for tests and for anyone asking what exists. */
export const UNREAD_REASONS = [
  'not_connected',
  'access_not_issued',
  'permission_not_granted',
  'microsoft_refused',
  'not_available_in_tenant',
  'throttled',
  'microsoft_unavailable',
  'dns_lookup_failed',
  'no_data_from_microsoft',
  'no_devices_onboarded',
  'incomplete_read',
  'unexpected_response',
  'beyond_check_scope',
  'exempt_list_unreadable',
  'defenssive_configuration',
  'check_failed',
  'unclassified',
  'awaiting_prerequisite',
] as const

// A Map, not an object: a word is server data, and "constructor" must not find a prototype member.
const GROUP_OF = new Map<string, UnreadGroupId>([
  ['permission_not_granted', 'permission'],
  ['access_not_issued', 'access'],
  ['not_connected', 'connection'],
  ['microsoft_refused', 'refused'],
  ['not_available_in_tenant', 'plan'],
  ['no_devices_onboarded', 'noDevices'],
  ['throttled', 'noAnswer'],
  ['microsoft_unavailable', 'noAnswer'],
  ['dns_lookup_failed', 'dns'],
  ['check_failed', 'ours'],
  ['defenssive_configuration', 'ours'],
  ['exempt_list_unreadable', 'ours'],
  ['beyond_check_scope', 'ours'],
  ['no_data_from_microsoft', 'unusable'],
  ['incomplete_read', 'unusable'],
  ['unexpected_response', 'unusable'],
  ['unclassified', 'unrecorded'],
  ['awaiting_prerequisite', 'waiting'],
])

/**
 * The group a recorded word belongs to. No word (null or absent) is a control recorded before 0159;
 * a word this build does not know claims nothing about its cause, so it is "Reason not recorded".
 */
export function groupOf(reason: string | null | undefined): UnreadGroupId {
  if (reason === null || reason === undefined) return 'legacy'
  return GROUP_OF.get(reason) ?? 'unrecorded'
}

/**
 * A control that is waiting on a prerequisite rather than unread: the withheld outcome (0152), or
 * its 0159 word. Both go to the calm waiting card, never under "could not be read".
 */
export function isWaiting(control: UnreadControl): boolean {
  return control.outcome === 'withheld' || control.reason === 'awaiting_prerequisite'
}

/** The order groups are shown in: what the customer can act on first. */
export const GROUP_ORDER: readonly UnreadGroupId[] = [
  'permission',
  'access',
  'connection',
  'refused',
  'plan',
  'noDevices',
  'noAnswer',
  'dns',
  'ours',
  'unusable',
  'unrecorded',
  'legacy',
]

/** Where Defenssive's permissions are re-approved and the tenant connection is checked. */
export const TENANT_SETTINGS = '/settings/tenant'

/**
 * The finding the sensor check raises when Microsoft Defender lists no devices (0159: the controls
 * that read per-device data say "no_devices_onboarded" in the same scan). The title is the
 * catalogue's (migration 0099) and is only the fallback: the dashboard prefers the live finding's.
 */
export const NO_DEVICES_FINDING = {
  key: 'endpoint.defender_sensor_unhealthy',
  title: 'Defender is not protecting every device it knows about',
} as const

/** Recommendations, searching for one finding by title -- the waiting card's pattern. */
export function recommendationsSearch(title: string): string {
  return `/recommendations?q=${encodeURIComponent(title)}`
}

/** The sentence a control recorded before 0159 has always been given. */
export const LEGACY_SENTENCE =
  'These were attempted but did not return a usable result — a permission may have changed, or ' +
  'Microsoft may have been unavailable. They are not counted as passing; the next complete scan ' +
  're-checks them.'

/** How one group reads on a page. */
export interface UnreadGroupCopy {
  id: UnreadGroupId
  heading: string
  sentence: string
  /** Where the customer goes to act on it, or null where there is nothing for them to do. */
  remedy: { label: string; to: string } | null
  /**
   * Nothing failed: the tenant lacks a thing (devices reporting, a plan feature) rather than a read
   * going wrong. A card made only of calm groups is drawn neutral, like the waiting card.
   */
  calm: boolean
}

/**
 * The words for one group.
 *
 * `permissions` names what a permission group lacks (from the controls in it); `findingTitle` is the
 * no-devices finding's title for its link. Both are ignored by the groups that do not use them.
 */
export function describeGroup(
  id: Exclude<UnreadGroupId, 'waiting'>,
  { permissions = [], findingTitle = NO_DEVICES_FINDING.title }: {
    permissions?: readonly string[]
    findingTitle?: string
  } = {},
): UnreadGroupCopy {
  const reapprove = { label: 'Re-approve permissions', to: TENANT_SETTINGS }

  switch (id) {
    case 'permission':
      return {
        id,
        heading: 'A permission Defenssive needs has not been approved',
        sentence: permissionSentence(permissions),
        remedy: reapprove,
        calm: false,
      }
    case 'access':
      return {
        id,
        heading: 'Microsoft would not give Defenssive access',
        sentence:
          'Microsoft declined to give Defenssive access to the service these controls read, and did not ' +
          "say why. Re-approving Defenssive's permissions is the first thing to try; if the next " +
          'assessment is still refused, contact us.',
        remedy: reapprove,
        calm: false,
      }
    case 'connection':
      return {
        id,
        heading: 'Defenssive was not connected to your Microsoft tenant',
        sentence:
          'When this assessment ran, the connection to your Microsoft tenant was not in a state that ' +
          'allows reading.',
        remedy: { label: 'Check the connection', to: TENANT_SETTINGS },
        calm: false,
      }
    case 'refused':
      return {
        id,
        heading: 'Microsoft refused the read',
        sentence:
          'Microsoft refused these reads and did not say why. It can mean a permission, a directory ' +
          'role, a licence or an access restriction in your tenant; the refusal alone does not tell ' +
          "us which. Re-approving Defenssive's permissions is the first thing to try; if it " +
          'persists, contact us.',
        remedy: reapprove,
        calm: false,
      }
    case 'plan':
      // Never "not licensed": that card (0158) is said only when the tenant's licences are absent
      // too. Here Microsoft said the feature is unavailable, and that is all that is known.
      return {
        id,
        heading: 'Not available in your Microsoft plan',
        sentence:
          'Microsoft reports that the feature these controls read is not available in your tenant, ' +
          'which usually means your plan does not include it. They stay unassessed on every ' +
          'assessment until it is.',
        remedy: null,
        calm: true,
      }
    case 'noDevices':
      return {
        id,
        heading: 'Waiting on devices reporting to Microsoft Defender',
        sentence:
          'Microsoft Defender lists no devices for your tenant, so it has nothing to report for these ' +
          'controls. They are not counted as passing, and nothing they found before has been closed. ' +
          'This is raised as its own finding.',
        remedy: { label: 'See the finding', to: recommendationsSearch(findingTitle) },
        calm: true,
      }
    case 'noAnswer':
      return {
        id,
        heading: 'Microsoft did not answer this time',
        sentence:
          "Microsoft's service did not answer in time, or asked Defenssive to slow down. Nothing " +
          'needs changing in your tenant; the next assessment tries again.',
        remedy: null,
        calm: false,
      }
    case 'dns':
      // Not "Microsoft", and not "nothing needs changing": a lookup that fails on every assessment
      // can be the domain's own DNS (review, 2026-09-26).
      return {
        id,
        heading: 'A public DNS lookup did not answer',
        sentence:
          'The public DNS lookup for one or more of your email domains did not return an answer, ' +
          'so these records could not be checked. This is usually temporary; if it happens on ' +
          "every assessment, check that your domains' DNS servers answer queries.",
        remedy: null,
        calm: false,
      }
    case 'ours':
      // Deliberately NOT "nothing needs changing in your tenant": an emergency account list that
      // could not be read, or a setting these checks do not read yet, is not an all-clear either.
      return {
        id,
        heading: "Not concluded on Defenssive's side",
        sentence:
          "These stopped on Defenssive's side — an error, a setting these checks do not read yet, or " +
          'your emergency account list could not be read — so they were not concluded. If it ' +
          'persists, contact us.',
        remedy: null,
        calm: false,
      }
    case 'unusable':
      return {
        id,
        heading: 'No usable answer',
        sentence:
          'The answer these checks received could not be judged — nothing was returned, it was ' +
          'too long to read in full, or it was in a form they do not recognise. Nothing is ' +
          'concluded from it; the next assessment reads again. If it persists, contact us.',
        remedy: null,
        calm: false,
      }
    case 'unrecorded':
      // Claims no cause at all: the word was not recorded, or is one this page does not know.
      return {
        id,
        heading: 'Reason not recorded',
        sentence:
          'These were attempted but did not return a usable result, and this assessment did not ' +
          'record why. They are not counted as passing; the next assessment checks them again.',
        remedy: null,
        calm: false,
      }
    case 'legacy':
      return {
        id,
        heading: 'Could not be read',
        sentence: LEGACY_SENTENCE,
        remedy: null,
        calm: false,
      }
  }
}

/** Where a permission is named, in this order; anything else after, alphabetically. */
const KNOWN_PERMISSIONS = [
  'Machine.Read.All',
  'SecurityRecommendation.Read.All',
  'Vulnerability.Read.All',
  'Score.Read.All',
]

/** The distinct permissions a set of controls names, in a stable order. */
export function permissionsOf(controls: readonly UnreadControl[]): string[] {
  const named = [...new Set(controls.map((c) => c.permission?.trim() ?? '').filter((p) => p !== ''))]
  const rank = (p: string) => {
    const at = KNOWN_PERMISSIONS.indexOf(p)
    return at === -1 ? KNOWN_PERMISSIONS.length : at
  }
  return named.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

/**
 * The permission group's sentence, naming what is missing: one permission, two ("X and Y"), or
 * more ("X, Y and Z"). None named still says a permission is missing, which the word itself proves.
 */
export function permissionSentence(permissions: readonly string[]): string {
  const many = permissions.length > 1
  const missing =
    permissions.length === 0
      ? 'a Microsoft Defender permission'
      : `the Microsoft Defender ${many ? 'permissions' : 'permission'} ${andList(permissions)}`
  const it = many ? 'them' : 'it'

  return (
    `The access Microsoft gives Defenssive in your tenant does not include ${missing}, which these ` +
    `controls need. An administrator can approve ${it} by re-approving Defenssive's permissions. ` +
    'The first assessment after that tries again (it can take up to an hour to apply); if Microsoft ' +
    `still refuses, your plan may not include ${it}.`
  )
}

/** "A", "A and B", "A, B and C". */
function andList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * One group present on a card: its words and its controls, in display order. Groups with no
 * controls are left out; the waiting controls are not here -- they have their own card.
 */
export interface UnreadSection extends UnreadGroupCopy {
  controls: UnreadControl[]
}

export function sectionsOf(
  controls: readonly UnreadControl[],
  findingTitle: string = NO_DEVICES_FINDING.title,
): UnreadSection[] {
  const byGroup = new Map<UnreadGroupId, UnreadControl[]>()
  for (const control of controls) {
    const id = groupOf(control.reason)
    const list = byGroup.get(id)
    if (list) list.push(control)
    else byGroup.set(id, [control])
  }

  return GROUP_ORDER.flatMap((id) => {
    const members = byGroup.get(id)
    if (!members || id === 'waiting') return []
    return [{
      ...describeGroup(id, { permissions: permissionsOf(members), findingTitle }),
      controls: members,
    }]
  })
}

/*
 * The same reasons as a short clause after "We couldn't read ...", for the Defender panel (whose
 * failure field carries these words on a reading taken since 0159). Worded to agree with the groups
 * above. '' where the word establishes no cause worth stating -- "unclassified" is "not recorded",
 * and an unknown word is not guessed at.
 */
const CLAUSE_OF = new Map<string, string>([
  ['not_connected', ': Defenssive was not connected to your Microsoft tenant'],
  [
    'access_not_issued',
    ": Microsoft would not give Defenssive access, and did not say why. Re-approving Defenssive's " +
      'permissions is the first thing to try',
  ],
  [
    'permission_not_granted',
    ': the access Microsoft issued to Defenssive here does not include the permission this needs. An ' +
      'administrator can grant admin consent to Defenssive again; it can take up to an hour to apply',
  ],
  [
    'microsoft_refused',
    ': Microsoft refused the read and did not say why (a permission, a directory role, a licence or ' +
      'an access restriction in your tenant can each cause it)',
  ],
  [
    'not_available_in_tenant',
    ': Microsoft reports it is not available in your tenant, which usually means your plan does not ' +
      'include it',
  ],
  ['throttled', ': Microsoft asked us to slow down'],
  ['microsoft_unavailable', ': Microsoft did not answer in time'],
  ['dns_lookup_failed', ': a public DNS lookup did not answer in time'],
  ['no_devices_onboarded', ': Microsoft Defender lists no devices for your tenant'],
  ['no_data_from_microsoft', ': Microsoft answered with nothing to read'],
  ['incomplete_read', ': the answer was too long to read in full'],
  ['unexpected_response', ': Microsoft answered in a form Defenssive does not recognise'],
  ['beyond_check_scope', ": it stopped on Defenssive's side"],
  ['exempt_list_unreadable', ": it stopped on Defenssive's side"],
  ['defenssive_configuration', ": it stopped on Defenssive's side"],
  ['check_failed', ": it stopped on Defenssive's side"],
])

/** The clause for a 0159 word, or '' (no word, "unclassified", or a word this build does not know). */
export function reasonClause(reason: string | null | undefined): string {
  if (reason === null || reason === undefined) return ''
  return CLAUSE_OF.get(reason) ?? ''
}
